import { authFetch, clearToken } from "../utils/auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Target, ChevronRight, ChevronLeft, Calendar, Clock, 
  MapPin, TrendingUp, Check, User
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = window.location.origin;

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    handicap: "",
    practice_days_per_week: 3,
    typical_session_length: "30",
    primary_environment: "range",
    skill_priorities: [],
    has_coach: false
  });

  const totalSteps = 3;
  const progressPercent = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step === 1 && !formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (step === 2 && formData.skill_priorities.length === 0) {
      toast.error("Please select at least one skill to focus on");
      return;
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const toggleSkillPriority = (skill) => {
    setFormData(prev => ({
      ...prev,
      skill_priorities: prev.skill_priorities.includes(skill)
        ? prev.skill_priorities.filter(s => s !== skill)
        : [...prev.skill_priorities, skill]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/onboarding/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("You're all set! Let's get started.");
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to complete setup");
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <span className="font-bold text-xl text-[#111]">GolfFlowPro</span>
          </div>
          <Badge className="bg-gray-200 text-[#555] border-gray-200">
            Step {step} of {totalSteps}
          </Badge>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="px-6 py-3 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <Progress value={progressPercent} className="h-1 bg-gray-200" />
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-6 py-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step 1: Name & Handicap */}
            {step === 1 && (
              <div className="space-y-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#111] mb-2">Welcome to GolfFlowPro</h1>
                  <p className="text-[#555]">Let's personalize your practice sessions</p>
                </div>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <Label className="text-[#111] mb-2 block">What should we call you?</Label>
                      <Input
                        data-testid="name-input"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] placeholder:text-[#555]"
                      />
                    </div>

                    <div>
                      <Label className="text-[#111] mb-2 block">Current handicap (optional)</Label>
                      <Input
                        data-testid="handicap-input"
                        type="number"
                        placeholder="e.g., 12"
                        value={formData.handicap}
                        onChange={(e) => setFormData({ ...formData, handicap: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] placeholder:text-[#555]"
                      />
                      <p className="text-[#555] text-sm mt-2">Helps us calibrate your missions</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 2: Skill Priorities */}
            {step === 2 && (
              <div className="space-y-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#111] mb-2">Your Focus Areas</h1>
                  <p className="text-[#555]">Select all skills you want to improve</p>
                </div>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "putting", label: "Putting", desc: "Reads, pace, short putts" },
                        { id: "chipping", label: "Chipping", desc: "Contact & trajectory" },
                        { id: "pitching", label: "Pitching", desc: "Distance control" },
                        { id: "irons", label: "Iron Play", desc: "Strike & dispersion" },
                        { id: "driving", label: "Driving", desc: "Start line & shape" },
                        { id: "course", label: "Course Mgmt", desc: "Decision making" }
                      ].map((skill) => (
                        <button
                          key={skill.id}
                          onClick={() => toggleSkillPriority(skill.id)}
                          className={`p-4 rounded-xl text-left transition-all border ${
                            formData.skill_priorities.includes(skill.id)
                              ? "bg-emerald-500/20 border-emerald-500/50"
                              : "bg-gray-100 border-gray-200 hover:bg-gray-200"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-[#111]">{skill.label}</span>
                            {formData.skill_priorities.includes(skill.id) && (
                              <Check className="w-4 h-4 text-emerald-700" />
                            )}
                          </div>
                          <span className="text-xs text-[#555]">{skill.desc}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Coach */}
            {step === 3 && (
              <div className="space-y-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#111] mb-2">Almost Ready!</h1>
                  <p className="text-[#555]">One more thing</p>
                </div>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <Label className="text-[#111] mb-3 block">Do you work with a golf coach?</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setFormData({ ...formData, has_coach: false })}
                          className={`py-3 px-4 rounded-xl text-center font-medium transition-all ${
                            !formData.has_coach
                              ? "bg-emerald-500 text-[#111]"
                              : "bg-gray-100 text-[#555] hover:bg-gray-200"
                          }`}
                        >
                          No
                        </button>
                        <button
                          onClick={() => setFormData({ ...formData, has_coach: true })}
                          className={`py-3 px-4 rounded-xl text-center font-medium transition-all ${
                            formData.has_coach
                              ? "bg-emerald-500 text-[#111]"
                              : "bg-gray-100 text-[#555] hover:bg-gray-200"
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                      {formData.has_coach && (
                        <p className="text-emerald-700 text-sm mt-2">
                          Great! You can add coach drills from your dashboard.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Navigation Buttons */}
      <footer className="px-6 py-6 pb-20 border-t border-gray-200 bg-gray-50">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 1}
            className="text-[#555] hover:text-[#111] disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <Button
            data-testid="next-btn"
            onClick={handleNext}
            disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8"
          >
            {loading ? (
              "Setting up..."
            ) : step === totalSteps ? (
              <>
                Start Practicing
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
}
